import React from 'react';
import { TableSortState } from '../../../models/tableSortState';
import { CircularProgress, Paper, Table, TableCell, TableRow, makeStyles } from '@material-ui/core';
import { HeaderCell, SecondaryAlert, TableBody, TableHead } from 'kagr-ui';
import * as styles from '../segments.module.scss';
import Pagination from '../../pagination/pagination';
import clsx from 'clsx';
import { useSegmentDependencies } from '../../../hooks/useSegmentDependencies';
import { DateTimeFormatter } from '../../../shared/date-format';

type SegmentDependenciesTableProps = {
  segmentId: string;
};

enum SegmentDependenciesSortFields {
  Name = 'name',
  ModifiedDate = 'lastModifiedDate',
  Description = 'description',
  NumberOfCustomers = 'totalCustomers',
  CreatedBy = 'createdByName'
}

const segmentDependenciesHeaders = [
  {
    title: 'Name',
    orderField: SegmentDependenciesSortFields.Name
  },
  {
    title: 'Modified Date',
    orderField: SegmentDependenciesSortFields.ModifiedDate
  },
  {
    title: 'Description',
    orderField: SegmentDependenciesSortFields.Description
  },
  {
    title: '# of Customers',
    orderField: SegmentDependenciesSortFields.NumberOfCustomers
  },
  {
    title: 'Created By',
    orderField: SegmentDependenciesSortFields.CreatedBy
  },
  {
    title: 'View Segment',
    orderField: null
  }
];

const useStyles = makeStyles(() => ({
  tablePadding: {
    padding: '25px 0'
  },
  tableHeader: {
    display: 'flex',
    justifyContent: 'end',
    alignItems: 'center',
    padding: '10px 30px 0'
  },
  dependenciesagination: {
    display: 'flex',
    justifyContent: 'flex-end',
    borderTop: '1px solid #DAD5D5'
  },
  noDependenciesContainer: {
    margin: '10rem',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    '& div': {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center'
    }
  },
  paperColumn: {
    flexDirection: 'column'
  },
  linkText: {
    textDecoration: 'none',
    fontWeight: 500,
    color: '#006DA8',
    cursor: 'pointer',
    border: 'none',
    background: 'none',
    padding: 0
  }
}));

const SegmentDependenciesTable = ({ segmentId }: SegmentDependenciesTableProps) => {
  const classes = useStyles();

  const [tableSort, setTableSort] = React.useState<TableSortState>({
    page: 1,
    pageSize: 15,
    orderColumn: SegmentDependenciesSortFields.Name,
    orderDirection: 'desc'
  });

  const segmentDependenciesQuery = useSegmentDependencies(segmentId, tableSort);

  function reorderHandler(column: string, direction: 'asc' | 'desc') {
    setTableSort((state) => ({ ...state, orderColumn: column, orderDirection: direction }));
  }

  if (!!segmentDependenciesQuery.data && segmentDependenciesQuery.data.totalItems === 0) {
    return (
      <div className={classes.noDependenciesContainer}>
        <div>
          <div className={styles.tableTitle}>This segment has no dependencies</div>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.root}>
      <section className={classes.tablePadding}>
        <Paper elevation={1} className={clsx(styles.paper, classes.paperColumn)}>
          <div className={styles.content}>
            <div className={classes.tableHeader}></div>
            {segmentDependenciesQuery.isLoading && !segmentDependenciesQuery.data && (
              <div className={styles.loading}>
                <CircularProgress />
              </div>
            )}
            {segmentDependenciesQuery.isError && (
              <div className={styles.error}>
                <SecondaryAlert
                  type="error"
                  message={`Failed to fetch results: ${segmentDependenciesQuery.error.message}`}
                />
              </div>
            )}
            {!!segmentDependenciesQuery.data && segmentDependenciesQuery.data.totalItems > 0 && (
              <div className={styles.tableWrapper}>
                <Table stickyHeader className={styles.table}>
                  <TableHead>
                    {segmentDependenciesHeaders.map((header) => (
                      <HeaderCell
                        key={header.title}
                        title={header.title}
                        orderField={header.orderField ? header.orderField : null}
                        orderColumn={header.orderField ? tableSort.orderColumn : null}
                        orderDirection={header.orderField ? tableSort.orderDirection : null}
                        reorderHandler={header.orderField ? reorderHandler : null}
                      />
                    ))}
                    <HeaderCell title=""></HeaderCell>
                  </TableHead>
                  <TableBody>
                    {segmentDependenciesQuery.data.totalItems === 0 && (
                      <tr>
                        <td colSpan={segmentDependenciesHeaders.length}>No results found</td>
                      </tr>
                    )}
                    {segmentDependenciesQuery.data.results.map((segmentDependency) => (
                      <TableRow key={segmentDependency.id}>
                        <TableCell padding="none">{segmentDependency.name}</TableCell>
                        <TableCell padding="none">
                          {segmentDependency.lastModifiedDate
                            ? DateTimeFormatter.format(new Date(segmentDependency.lastModifiedDate))
                            : ''}
                        </TableCell>
                        <TableCell padding="none">{segmentDependency.description}</TableCell>
                        <TableCell padding="none">{segmentDependency.totalCustomers.toLocaleString()}</TableCell>
                        <TableCell padding="none">{segmentDependency.createdByName}</TableCell>
                        <TableCell padding="none">
                          <button
                            onClick={() =>
                              window.open(`${window.location.origin}/segments/${segmentDependency.id}`, '_blank')
                            }
                            className={classes.linkText}
                          >
                            View in New Tab
                          </button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
            {segmentDependenciesQuery.data && (
              <div className={`${styles.paginationFooter} ${classes.dependenciesagination}`}>
                <Pagination
                  totalPages={Math.ceil(segmentDependenciesQuery.data.totalItems / tableSort.pageSize)}
                  pageSize={tableSort.pageSize}
                  currentPage={tableSort.page}
                  disabled={segmentDependenciesQuery.isPreviousData && segmentDependenciesQuery.isFetching}
                  handlePageChange={(page) => setTableSort((state) => ({ ...state, page }))}
                  handlePageSizeChange={(pageSize) =>
                    setTableSort((state) => ({
                      ...state,
                      page: 1,
                      pageSize
                    }))
                  }
                />
              </div>
            )}
          </div>
        </Paper>
      </section>
    </div>
  );
};

export default SegmentDependenciesTable;
